import { useRouter as useNextRouter } from "next/router";
import { GlassSurface, HeaderButton, InHeader, Subtitle1, Theme, useMedia, VisuallyHidden, XStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { homeUrl } from "@medbillai/utils";
import { sectionExitSteps, takeOpenedInApp, wasOpenedInApp } from "../../lib/openedInApp";
import { breadcrumbTrail, isSheetSection, parentRoute } from "../../lib/webSections";
import { Breadcrumbs } from "./Breadcrumbs";
import type { ScreenHeaderProps } from "./types";
const placement = {
  solid: {
    position: "sticky",
    top: 0
  },
  blur: {
    position: "sticky",
    top: 0
  },
  // Laid over the content, like native's transparent header.
  transparent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0
  }
} as const;

/** The mobile web header row, or desktop's breadcrumb bar on pages with a trail. Render it first in the screen. */
export const ScreenHeader = ({
  title,
  label,
  titleHidden = false,
  leading = "back",
  fallbackTo,
  popOnce = false,
  onBack,
  trailing,
  tone = "default",
  large = false,
  background = "solid"
}: ScreenHeaderProps) => {
  const {
    back,
    replace
  } = useRouter();
  const {
    pathname,
    asPath
  } = useNextRouter();
  const media = useMedia();
  const inSheet = isSheetSection(pathname);
  const sheetRoot = inSheet && pathname.lastIndexOf("/") === 0;
  const goBack = () => {
    onBack?.();
    if (popOnce ? takeOpenedInApp(asPath) : wasOpenedInApp(asPath)) {
      back();
    } else {
      replace(fallbackTo ?? parentRoute(pathname));
    }
  };
  const close = () => {
    const steps = sectionExitSteps();
    if (steps) {
      window.history.go(-steps);
    } else {
      replace(homeUrl());
    }
  };
  const Row = background === "blur" ? GlassSurface : XStack;
  const hiddenName = typeof title !== "string" ? label : titleHidden ? title : undefined;
  // Tab roots: mobile web shows the logo bar instead.
  if (large) {
    return null;
  }
  const trail = breadcrumbTrail(pathname);
  if (media.gtSm && trail.length > 0) {
    return <Breadcrumbs trail={trail} current={typeof title === "string" ? title : label} trailing={trailing} />;
  }
  return <Theme name={tone === "inverse" ? "inverse" : null} data-sentry-element="Theme" data-sentry-component="ScreenHeader" data-sentry-source-file="ScreenHeader.tsx">
      <InHeader tone={tone} data-sentry-element="InHeader" data-sentry-source-file="ScreenHeader.tsx">
        <Row flexDirection="row" alignItems="center" justifyContent="space-between" alignSelf="stretch" height={inSheet ? "$si7" : "$si5.5"} paddingHorizontal="$sp2" backgroundColor={background === "solid" ? tone === "inverse" ? "$surfaceInverse" : "$surface1" : undefined} zIndex={2} {...placement[background]} $gtSm={{
        display: "none"
      }} data-sentry-element="Row" data-sentry-source-file="ScreenHeader.tsx">
          {hiddenName ? <VisuallyHidden>{hiddenName}</VisuallyHidden> : null}
          <XStack width="$si6" data-sentry-element="XStack" data-sentry-source-file="ScreenHeader.tsx">
            {leading === "close" ? <HeaderButton kind="close" onPress={close} /> : leading === "back" && !sheetRoot ? <HeaderButton kind="back" onPress={goBack} /> : null}
          </XStack>
          {titleHidden ? null : typeof title === "string" ? <Subtitle1 numberOfLines={1} flexShrink={1} color={tone === "inverse" ? "$textInverse" : undefined}>
              {title}
            </Subtitle1> : title}
          <XStack width="$si6" justifyContent="flex-end" data-sentry-element="XStack" data-sentry-source-file="ScreenHeader.tsx">
            {trailing !== undefined ? trailing : inSheet && leading !== "close" ? <HeaderButton kind="close" onPress={close} /> : null}
          </XStack>
        </Row>
      </InHeader>
    </Theme>;
};